import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  GraduationCap, Lock, PlayCircle, Clock, BookOpen, CheckCircle2, ArrowRight, Award, ClipboardCheck, Sparkles, RotateCcw,
} from 'lucide-react';
import { SectionHeader, EmptyState, PageSkeleton, LoadError } from '@/components/dashboard/MetricCard';
import { academyApi, fmtMinutes, ACADEMY_COURSE_ID, blockParts } from '@/services/academyApi';

/*
  FHC Academy — UNA sola academia (decisión de Samuel, 09/10/2026): el curso oficial con sus bloques 0-7.
  Bloque 0 → todos (free incluido) · 1-2 → Access, PRO y Premium · 3-7 → PRO y Premium.
  El acceso lo decide el servidor (routes/academy.py) y no envía contenido de lo bloqueado: aquí solo se
  pinta el estado de cada bloque y el CTA. Cada bloque abre el reproductor (DashboardCurso) en su clase.
*/

export const courseHref = (courseId, lessonId) =>
  `/dashboard/academia/${encodeURIComponent(courseId)}${lessonId ? `?leccion=${encodeURIComponent(lessonId)}` : ''}`;
const quizHref = (courseId, quizId) => `/dashboard/academia/${encodeURIComponent(courseId)}?quiz=${encodeURIComponent(quizId)}`;
const MEMBERSHIPS_HREF = '/dashboard#membresias';

const btnPrimary = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[#c9a84c] px-4 py-2.5 text-sm font-semibold text-[#0a1628] hover:bg-[#d6b75a] transition-colors';
const btnGhost = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-medium text-gray-200 hover:bg-white/[0.05] transition-colors';
const btnGold = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[#c9a84c]/40 px-4 py-2.5 text-sm font-semibold text-[#e3c87a] hover:bg-[#c9a84c]/10 transition-colors';

function ProgressBar({ pct, delay = 0, className = '' }) {
  return (
    <div className={`w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden ${className}`} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7, delay, ease: 'easeOut' }}
        className="h-full bg-gradient-to-r from-[#c9a84c] to-[#dbb85e] rounded-full" />
    </div>
  );
}

// Estado de un bloque para este usuario, calculado con lo que devuelve el servidor
function blockState(m) {
  const lessons = m.lessons || [];
  const done = lessons.filter((l) => l.completed).length;
  const items = lessons.length + (m.quiz ? 1 : 0);
  const doneItems = done + (m.quiz?.passed ? 1 : 0);
  let status = 'available';
  if (m.locked) status = m.lock_reason === 'plan' ? 'plan' : 'sequence';
  else if (items > 0 && doneItems >= items) status = 'done';
  else if (doneItems > 0 || m.quiz?.attempts) status = 'progress';
  // Dónde entrar: primera clase pendiente; si no quedan, el quiz por aprobar; si no, la primera clase
  const pending = lessons.find((l) => !l.completed);
  const target = pending ? { lesson: pending.id }
    : (m.quiz && !m.quiz.passed) ? { quiz: m.id } : lessons[0] ? { lesson: lessons[0].id } : null;
  return {
    status, done, total: lessons.length, pct: items ? Math.round((doneItems / items) * 100) : 0, target,
    minutes: lessons.reduce((a, l) => a + (Number(l.duration_min) || 0), 0),
  };
}

const STATUS_PILL = {
  done: ['Completado', 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300'],
  progress: ['En curso', 'border-[#c9a84c]/30 bg-[#c9a84c]/10 text-[#e3c87a]'],
  available: ['Disponible', 'border-white/10 bg-white/[0.04] text-gray-200'],
  sequence: ['Se abre al aprobar el anterior', 'border-white/10 bg-white/[0.03] text-gray-400'],
  plan: ['Bloqueado', 'border-white/10 bg-white/[0.03] text-gray-400'],
};

function upgradeCta(course, planName) {
  return course.viewer_member ? `Mejorar a ${planName}` : 'Hazte miembro';
}

function BlockCard({ course, m, index, featured, delay }) {
  const st = blockState(m);
  const { number, name } = blockParts(m.title, index);
  const [pillText, pillCls] = STATUS_PILL[st.status];
  const plan = st.status === 'plan';
  const href = st.target ? (st.target.quiz ? quizHref(course.id, st.target.quiz) : courseHref(course.id, st.target.lesson)) : courseHref(course.id);
  const intro = featured && !m.locked ? m.lessons?.[0]?.description : null;
  const NumIcon = st.status === 'done' ? CheckCircle2 : m.locked ? Lock : null;
  return (
    <motion.article initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay }}
      aria-labelledby={`block-${m.id}-title`} data-testid={`academy-block-${number}`}
      className={`relative flex h-full flex-col gap-4 overflow-hidden rounded-2xl border p-5 sm:p-6 ${featured
        ? 'border-[#c9a84c]/30 bg-gradient-to-br from-[#c9a84c]/[0.08] via-white/[0.02] to-transparent lg:col-span-2'
        : plan ? 'border-white/[0.05] bg-white/[0.015]' : 'border-white/[0.06] bg-white/[0.02]'}`}>
      {featured && <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#c9a84c]/10 blur-3xl" />}
      <div className="relative flex items-start gap-4">
        <span aria-hidden="true" className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-xl font-semibold tabular-nums ${
          st.status === 'done' ? 'bg-emerald-500/10 text-emerald-300'
            : m.locked ? 'bg-white/[0.04] text-gray-500' : 'bg-[#c9a84c]/15 text-[#e3c87a]'}`}>
          {NumIcon ? <NumIcon size={20} /> : number}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-[#c9a84c]">Bloque {number}</p>
            {featured && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#c9a84c] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#0a1628]">
                <Sparkles size={11} /> Empieza aquí
              </span>
            )}
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${pillCls}`}>
              {plan && m.min_plan_name ? `${pillText} · ${m.min_plan_name}` : pillText}
            </span>
          </div>
          <h3 id={`block-${m.id}-title`} className={`mt-1 font-semibold leading-snug ${m.locked ? 'text-gray-300' : 'text-white'} ${featured ? 'text-lg sm:text-xl' : 'text-base'}`}>
            {name}
          </h3>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1"><BookOpen size={13} /> {st.total} {st.total === 1 ? 'clase' : 'clases'}</span>
            {st.minutes > 0 && <span className="inline-flex items-center gap-1"><Clock size={13} /> {fmtMinutes(st.minutes)}</span>}
            {m.quiz && <span className="inline-flex items-center gap-1"><ClipboardCheck size={13} /> Quiz{m.quiz.passed ? ' aprobado' : ''}</span>}
            {!m.quiz && featured && <span>Abierto a todos los usuarios registrados</span>}
          </p>
        </div>
      </div>

      {intro && <p className="relative text-sm leading-relaxed text-gray-300 line-clamp-4 sm:line-clamp-3">{intro}</p>}

      {!m.locked && st.total > 0 && (
        <div className="relative space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">{st.done} de {st.total} {st.total === 1 ? 'clase' : 'clases'}{m.quiz ? (m.quiz.passed ? ' · quiz aprobado' : ' · quiz pendiente') : ''}</span>
            <span className="text-[#c9a84c] font-medium tabular-nums">{st.pct} %</span>
          </div>
          <ProgressBar pct={st.pct} delay={delay + 0.1} />
        </div>
      )}

      <div className="relative mt-auto flex flex-col gap-2 sm:flex-row sm:items-center">
        {plan ? (
          <>
            <Link to={MEMBERSHIPS_HREF} className={btnPrimary} data-testid={`academy-block-${number}-upgrade`}>
              {upgradeCta(course, m.min_plan_name)} <ArrowRight size={15} />
            </Link>
            <Link to={href} className={btnGhost}>Ver temario</Link>
            {!course.viewer_member && m.min_plan_name && <span className="text-xs text-gray-500 sm:ml-1">Incluido desde la membresía {m.min_plan_name}</span>}
          </>
        ) : st.status === 'sequence' ? (
          <>
            <Link to={href} className={btnGhost}>Ver temario</Link>
            <span className="text-xs text-gray-500">Aprueba el quiz del bloque anterior para abrirlo.</span>
          </>
        ) : (
          <Link to={href} className={featured ? btnPrimary : st.status === 'done' ? btnGhost : btnGold} data-testid={`academy-block-${number}-open`}>
            {st.status === 'done' ? <><RotateCcw size={15} /> Repasar</>
              : st.status === 'progress' ? <>Continuar <ArrowRight size={15} /></>
                : featured && st.total === 1 ? <><PlayCircle size={16} /> Ver la masterclass</>
                  : <>Empezar <ArrowRight size={15} /></>}
          </Link>
        )}
      </div>
    </motion.article>
  );
}

function AcademyHero({ course }) {
  const lessons = course.modules.flatMap((m, mi) => (m.lessons || []).map((l) => ({ ...l, mi, module: m })));
  const next = lessons.find((l) => l.id === course.next_lesson_id) || null;
  const anyPending = lessons.some((l) => !l.locked && !l.completed)
    || course.modules.some((m) => !m.locked && m.quiz && !m.quiz.passed);
  const started = course.completed_lessons > 0 || course.quizzes_passed > 0;
  const nextBlock = next ? blockParts(next.module.title, next.mi) : null;
  const gap = course.plan_locked_modules > 0 && (course.upgrade_plan_name || course.min_plan_name);
  return (
    <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      aria-labelledby="academy-hero-title" data-testid="academy-hero"
      className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-gradient-to-br from-[#0d1829] via-[#0a1628] to-[#060d1b] p-5 sm:p-8">
      <div className="pointer-events-none absolute -top-32 -left-24 h-72 w-72 rounded-full bg-[#c9a84c]/10 blur-3xl" />
      <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:items-center">
        <div className="space-y-3">
          <p className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#c9a84c]">
            <GraduationCap size={14} /> FHC Academy
          </p>
          <h2 id="academy-hero-title" className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">Tu recorrido, bloque a bloque</h2>
          {course.description && <p className="max-w-2xl text-sm leading-relaxed text-gray-300 line-clamp-4">{course.description}</p>}
          <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:flex-wrap">
            {next && (
              <Link to={courseHref(course.id, next.id)} className={btnPrimary} data-testid="academy-continue">
                <PlayCircle size={16} />
                {!started ? `Empezar por el Bloque ${nextBlock.number}` : anyPending ? 'Continuar donde lo dejaste' : 'Repasar'}
              </Link>
            )}
            {course.certificate && (
              <Link to={`/dashboard/academia/diploma/${encodeURIComponent(course.certificate.code)}`} className={btnGold} data-testid="academy-diploma-link">
                <Award size={15} /> Ver mi diploma
              </Link>
            )}
            {gap && (
              <Link to={MEMBERSHIPS_HREF} className={btnGhost} data-testid="academy-upgrade">
                <Lock size={14} /> {upgradeCta(course, course.upgrade_plan_name || course.min_plan_name)}
              </Link>
            )}
          </div>
          {next && started && anyPending && (
            <p className="text-xs text-gray-400">Bloque {nextBlock.number} · {next.title}{next.duration_min ? ` · ${fmtMinutes(next.duration_min)}` : ''}</p>
          )}
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-5 space-y-3">
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider font-semibold text-gray-400">Tu progreso</span>
            <span className="text-3xl font-semibold tabular-nums text-white">{course.progress_pct}<span className="text-base text-gray-400"> %</span></span>
          </div>
          <ProgressBar pct={course.progress_pct} />
          <dl className="grid grid-cols-3 gap-2 pt-1 text-center">
            <div><dt className="text-[11px] text-gray-500">Clases</dt><dd className="text-sm font-medium text-white tabular-nums">{course.completed_lessons}/{course.total_lessons}</dd></div>
            <div><dt className="text-[11px] text-gray-500">Quizzes</dt><dd className="text-sm font-medium text-white tabular-nums">{course.quizzes_passed}/{course.quizzes_total}</dd></div>
            <div><dt className="text-[11px] text-gray-500">Duración</dt><dd className="text-sm font-medium text-white">{fmtMinutes(course.duration_total_min)}</dd></div>
          </dl>
          {gap && (
            <p className="text-xs leading-relaxed text-gray-400">
              {course.viewer_member
                ? `${course.plan_locked_modules} ${course.plan_locked_modules === 1 ? 'bloque requiere' : 'bloques requieren'} la membresía ${course.upgrade_plan_name}.`
                : 'Con una membresía sigues con los bloques 1 y 2 (Access) o con el recorrido completo y el diploma (PRO y Premium).'}
            </p>
          )}
        </div>
      </div>
    </motion.section>
  );
}

function FinalCard({ course }) {
  const fq = course.final_quiz;
  if (!fq) return null;
  const text = course.certificate ? 'Has completado la academia. Tu diploma es verificable con su código.'
    : fq.available ? `Ya puedes hacer la evaluación final: ${fq.question_count} preguntas, aprueba con un ${fq.pass_pct} %.`
      : fq.blocked_reason === 'plan' ? 'La evaluación final y el diploma forman parte del recorrido completo (PRO y Premium).'
        : 'Se abre al aprobar los quizzes de todos los bloques.';
  return (
    <section aria-labelledby="academy-final-title" data-testid="academy-final"
      className="flex flex-col gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 sm:flex-row sm:items-center sm:p-6">
      <span className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${course.certificate ? 'bg-[#c9a84c]/20 text-[#e3c87a]' : 'bg-white/[0.04] text-gray-400'}`}>
        <Award size={20} />
      </span>
      <div className="min-w-0 flex-1">
        <h3 id="academy-final-title" className="font-semibold text-white">Evaluación final y diploma</h3>
        <p className="mt-0.5 text-sm text-gray-400">{text}</p>
      </div>
      {course.certificate ? (
        <Link to={`/dashboard/academia/diploma/${encodeURIComponent(course.certificate.code)}`} className={btnGold}><Award size={15} /> Ver mi diploma</Link>
      ) : fq.available ? (
        <Link to={quizHref(course.id, 'final')} className={btnPrimary}>Hacer la evaluación <ArrowRight size={15} /></Link>
      ) : fq.blocked_reason === 'plan' ? (
        <Link to={MEMBERSHIPS_HREF} className={btnGhost}>{upgradeCta(course, course.upgrade_plan_name || 'PRO')}</Link>
      ) : null}
    </section>
  );
}

export default function DashboardAcademia() {
  const [state, setState] = useState({ loading: true, course: null, others: [] });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setState((s) => ({ ...s, loading: true }));
    try {
      const list = await academyApi.courses();
      const main = list.find((c) => c.id === ACADEMY_COURSE_ID) || list[0] || null;
      const course = main ? await academyApi.course(main.id) : null;
      setState({ loading: false, course, others: list.filter((c) => c !== main) });
    } catch (e) {
      console.error(e);
      setError(e.message || 'Error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const header = <SectionHeader title="Academia" subtitle="Tu formación en el club, a tu ritmo" />;
  if (error) return <div className="space-y-6" data-testid="dashboard-academia">{header}<LoadError onRetry={load} /></div>;
  if (state.loading) return <PageSkeleton cards={2} rows={4} />;
  const { course, others } = state;

  return (
    <div className="space-y-6" data-testid="dashboard-academia">
      {header}
      {!course ? (
        <EmptyState icon={GraduationCap} title="La Academia se está preparando" text="Muy pronto tendrás aquí la formación del club, bloque a bloque." />
      ) : (
        <>
          <AcademyHero course={course} />
          <section aria-labelledby="academy-blocks-title" className="space-y-3">
            <h3 id="academy-blocks-title" className="text-sm font-semibold uppercase tracking-wider text-gray-400">Bloques</h3>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {course.modules.map((m, i) => (
                <BlockCard key={m.id} course={course} m={m} index={i} featured={i === 0 && !m.quiz} delay={Math.min(i, 8) * 0.04} />
              ))}
            </div>
          </section>
          <FinalCard course={course} />
          {others.length > 0 && (
            <section aria-labelledby="academy-more-title" className="space-y-3">
              <h3 id="academy-more-title" className="text-sm font-semibold uppercase tracking-wider text-gray-400">Más formación</h3>
              <div className="grid gap-2">
                {others.map((c) => (
                  <Link key={c.id} to={courseHref(c.id)}
                    className="flex min-h-[56px] items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 hover:border-[#c9a84c]/25 transition-colors">
                    {c.locked ? <Lock size={16} className="flex-shrink-0 text-gray-500" /> : <BookOpen size={16} className="flex-shrink-0 text-[#c9a84c]" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-white">{c.title}</span>
                      <span className="block text-xs text-gray-500">{c.total_lessons} {c.total_lessons === 1 ? 'clase' : 'clases'}{c.locked && c.min_plan_name ? ` · membresía ${c.min_plan_name}` : ''}</span>
                    </span>
                    <ArrowRight size={15} className="flex-shrink-0 text-gray-500" />
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
